import React, { useEffect, useState } from "react";
import { IconButton, Tooltip, makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import { fetchCreativePreviewInfo } from "../../services/sql/creativePreviewInfo";
import { fetchCreativesByIds } from "../../services/dcm/creatives";
import { buildCreativePreviewUrl } from "../../utils/dataUtils";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  tableCell: {
    wordBreak: "break-all"
  }
}));

export const AssignedCreativesList = (props) => {
  const classes = useStyles();
  let ads = useSelector((state) => state.dcmAds);
  let placements = useSelector((state) => state.dcmPlacements);
  let assignedCreatives = useSelector((state) => state.dcmAssignedCreatives);
  let [creativesAssignedToPlacement, setCreativesAssignedToPlacement] =
    useState([]);
  let [creativeIds, setCreativeIds] = useState([]);
  let [dbCreativeInfo, setDbCreativeInfo] = useState([]);
  let [dcmCreativeDetails, setDcmCreativeDetails] = useState({});

  useEffect(() => {
    let assignedCreativesState = [];
    let creativeIdsState = [];

    if (props.placementId !== "") {
      for (const ad of ads.adsByPlacementId[`${props.placementId}`]) {
        const isDefaultCreative = ad.type === "AD_SERVING_DEFAULT_AD";
        if (ad.creativeRotation) {
          ad.creativeRotation.creativeAssignments.map((creative) => {
            creative.isDefaultCreative = isDefaultCreative;
            creative.adType = ad.type;
            assignedCreativesState.push(creative);
            creativeIdsState.push(creative.creativeId);
          });
        }
      }

      setCreativesAssignedToPlacement(assignedCreativesState);
      setCreativeIds(creativeIdsState);
    } else {
      setCreativesAssignedToPlacement([]);
    }
  }, [ads.adsByPlacementId, props.placementId]);

  useEffect(() => {
    if (creativeIds.length > 0) {
      // Fetch SQL previews as fallback
      fetchCreativePreviewInfo(creativeIds).then((response) => {
        setDbCreativeInfo(response);
      });

      // Fetch full creative details from GCM (includes creativeAssets for on-the-fly previews)
      fetchCreativesByIds(creativeIds).then((response) => {
        const map = {};
        response.forEach((c) => { map[c.id] = c; });
        setDcmCreativeDetails(map);
      }).catch(() => {});
    }
  }, [creativeIds]);

  const getPreviewData = (dcmCreative) => {
    if (!dcmCreative) return null;

    // Prefer GCM on-the-fly URL
    const detail = dcmCreativeDetails[dcmCreative.id];
    if (detail) {
      const built = buildCreativePreviewUrl(detail);
      if (built) {
        return {
          url: built.url,
          previewType: built.previewType,
          creative: detail
        };
      }
    }

    // Fall back to SQL Glops preview
    const dbCreative = dbCreativeInfo.find((c) => c.Creative_ID == dcmCreative.id);
    if (dbCreative?.Glops_Creative_Preview_Link) {
      return {
        url: dbCreative.Glops_Creative_Preview_Link,
        previewType: "iframe",
        creative: dcmCreative
      };
    }

    return null;
  };

  const handlePreviewClick = (dcmCreative, adCreative) => {
    let { setCreativePreviewURL, setPreviewDialogOpen, handleCloseDialog } = props;
    const preview = getPreviewData(dcmCreative);
    if (!preview) return;

    const detail = dcmCreativeDetails[dcmCreative.id] || dcmCreative;

    props.setSelectedCreativePreview({
      ...detail,
      previewType: preview.previewType,
      creativeName: dcmCreative.name,
      creativeId: dcmCreative.id,
      creativeType: dcmCreative.type,
      startTime: adCreative.startTime,
      endTime: adCreative.endTime,
      adType: adCreative.adType,
      weight: adCreative.weight
    });
    setCreativePreviewURL(preview.url);
    handleCloseDialog();
    setPreviewDialogOpen(true);
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Preview</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Weight</TableCell>
          <TableCell>URLs Assigned</TableCell>
          <TableCell>Start</TableCell>
          <TableCell>End</TableCell>
          <TableCell>Is Default Ad</TableCell>
          <TableCell>Ad Type</TableCell>
          {/* <TableCell>Hard Cut-Off?</TableCell> */}
        </TableRow>
      </TableHead>
      {creativesAssignedToPlacement
        .sort((adCreative1, adCreative2) => {
          // move default creatives to the beginning
          if (adCreative1.isDefaultCreative && !adCreative2.isDefaultCreative) {
            return -1;
          }
          return 0;
        })
        .map((adCreative, index) => {
          let dcmCreative = assignedCreatives.byId[`${adCreative.creativeId}`];
          let startDate = new Date(adCreative.startTime);
          let endDate = new Date(adCreative.endTime);

          const formatDate = (input) => {
            let date = dayjs(input);

            if (date.isValid()) {
              return date.format("M/D/YYYY");
            } else {
              return "N/A";
            }
          };

          const preview = getPreviewData(dcmCreative);

          return (
            <TableBody key={`creative-${index}-${dcmCreative.id}`}>
              <TableRow>
                <TableCell>{dcmCreative.id}</TableCell>
                <TableCell>
                  {preview ? (
                      <IconButton onClick={() => handlePreviewClick(dcmCreative, adCreative)}>
                        <Visibility />
                      </IconButton>
                  ) : (
                    <Tooltip title="No preview available">
                      <VisibilityOff />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {dcmCreative.name}
                </TableCell>
                <TableCell>{adCreative.weight}</TableCell>
                <TableCell className={classes.tableCell}>
                  {adCreative.clickThroughUrl.computedClickThroughUrl}
                </TableCell>
                <TableCell>{formatDate(startDate)}</TableCell>
                <TableCell>{formatDate(endDate)}</TableCell>
                <TableCell
                  style={{
                    color: adCreative.isDefaultCreative ? "green" : "black"
                  }}
                >
                  {adCreative.isDefaultCreative.toString()}
                </TableCell>
                <TableCell>{adCreative.adType.toString()}</TableCell>
              </TableRow>
            </TableBody>
          );
        })}
    </Table>
  );
};
